/* eslint-disable max-len */
import { useEffect, useState } from 'react';

import Head from 'next/head';
import { Container } from 'reactstrap';
import { useRecoilValue } from 'recoil';

import CampaignSliderTW from './components/tw-campaign-slider';

import { getReferralAmount } from '../../apis/referral';
import Collapse from '../../components/includes/collapse';
import ImageViewerModal from '../../components/includes/image-viewer-modal';
import Layout from '../../components/layout/layout';
import { pageviewEvent } from '../../helpers/referral-segment';
import useI18n from '../../hooks/use-i18n';
import { contentLanguageMap } from '../../language.config';
import { config as configState } from '../../recoil/atoms/config';
import { screenTimeEvent } from '../../utils/utils';

import '../../styles/globals/globals.scss';
import '../../styles/referrals-mechanism.scss';

const ReferralsMechanism = () => {
  const i18n = useI18n();
  const { t } = i18n;
  const referralLandingPage = i18n.t('referrer');
  const pageMetaData = i18n.t('metadata');
  const codes = i18n.activeLocale.split('-');
  const country = codes.length === 2 ? codes[0] : 'sg';
  const lang = codes[1] || 'en';
  const LAMBDA_SERVICE_TYPE = 'CP_SETTINGS';
  const [isNotifyBarOpen, setIsNotifyBarOpen] = useState(false);
  const configData = useRecoilValue(configState);

  const [isMobile, setIsMobile] = useState(false);
  const [referralAmount, setReferralAmount] = useState({
    referredAmount: '',
    referrerAmount: ''
  });
  const [activeImage, setImageActive] = useState({ activeSlide: 0, isModalOpen: false });

  const entryTime = new Date().getTime();

  useEffect(() => {
    setIsMobile(window.innerWidth < 1024);
    loadReferralAmountDetails();
    const deviceViewed = window.innerWidth < 1024 ? 'Mobile' : 'Desktop';
    pageviewEvent('General Mechanism LP Viewed', { device: deviceViewed });
    return () => {
      screenTimeEvent(entryTime, 'General Mechanism LP', country);
    };
  }, []);
  useEffect(() => {
    const notifybar = document.querySelector('.product-notify-bar');
    setIsNotifyBarOpen(typeof (notifybar) !== 'undefined' && notifybar != null);
  }, [configData]);

  const loadReferralAmountDetails = async () => {
    const response = await getReferralAmount(LAMBDA_SERVICE_TYPE, country.toUpperCase(), lang);
    if (response?.referred_amount || response?.referrer_amount) {
      setReferralAmount({
        referredAmount: response.referred_amount || '',
        referrerAmount: response.referrer_amount || ''
      });
    }
  };

  const getFAQContent = (referralFaq) => (
    `<div class="f-md">
      <span class="content">
        ${t(referralFaq, { referrer_amount: referralAmount.referrerAmount, referred_amount: referralAmount.referredAmount })}
      </span>
    </div>`
  );

  const [isCollapseActive, setIsCollapseActive] = useState(false);

  return (
    <Layout
      page="referral-mechanism"
      title={pageMetaData.title}
      description={pageMetaData.description}
      keywords={pageMetaData.keywords}
      pageEnabledMarkets={['sg', 'vn', 'my', 'hk', 'tw', 'th', 'jp']}
      pageUrl="referrals-mechanism"
    >
      <div className={`wrapper referral-page ${isNotifyBarOpen ? 'notifybar-active' : ''}`} id="main-sg-rm">
        <Head>
          <title>{referralLandingPage.meta_title}</title>
          <meta charSet="utf-8" />
          <meta name="viewport" content="initial-scale=1.0, width=device-width" />
          <meta name="description" content={referralLandingPage.meta_description} />
          <meta name="keywords" content={referralLandingPage.page_keywords} />
          <meta name="author" content="Zenyum" />
          <meta httpEquiv="content-language" content={lang} />
          <meta property="og:title" content={referralLandingPage.meta_title} />
          <meta property="og:description" content={referralLandingPage.meta_description} />
          <meta property="og:image" content={`${process.env.NEXT_PUBLIC_BASE_URL}img/FB_zenyum_02.jpg`} />
          <meta httpEquiv="content-language" content={contentLanguageMap[i18n.activeLocale]} />

          <link rel="icon" href="/favicon_32x32.png" sizes="32x32" />
          <link rel="icon" href="/favicon_192x192.png" sizes="192x192" />
          <link rel="apple-touch-icon" href="/img/favicon_32x32.png" />
          {lang !== 'vi' && lang !== 'th' && lang !== 'zh' && (
          <link rel="stylesheet" href="/fonts/fonts.css" />
          )}
          {lang === 'vi' && <link rel="stylesheet" href="/fonts/fonts-vi.css" />}
          {lang === 'th' && <link rel="stylesheet" href="/fonts/fonts-th.css" />}
          {lang === 'zh' && <link rel="stylesheet" href="/fonts/fonts-zh.css" />}
          <link rel="stylesheet" href="/vendor/bootstrap/css/bootstrap.min.css" async={true} />
          <link rel="stylesheet" href="/vendor/slick/slick.min.css" />
          <link rel="stylesheet" href="/vendor/slick/slick-theme.min.css" defer={true} />

        </Head>
        <main className="main-wrap">
          {activeImage.isModalOpen && (<ImageViewerModal
            imageSrc={`/img/referrer/revamp/tw-campaign/campaign-image-${activeImage.activeSlide}-mobile.png`}
            setImageActive={setImageActive}
          />)}
          <section className="referral-banner-section">
            <div className="referral-banner-content">
              <h1
                data-key="referrer::heading"
                data-lokalise=""
              >
                {t('referrer.heading', { referrer_amount: referralAmount.referrerAmount })}
              </h1>
              <p
                data-key="referrer::content"
                data-lokalise=""
              >
                {t('referrer.content', { referred_amount: referralAmount.referredAmount })}
              </p>
              {isMobile && (
                <a
                  className="assessment-button"
                  data-key="referrer::button_mobile"
                  data-lokalise=""
                  onClick={() => screenTimeEvent(entryTime, 'General Mechanism LP', country)}
                  href="https://zenyum.onelink.me/4taf/referrerPage"
                >
                  {referralLandingPage.button_mobile}
                </a>)}
              <div className="referral-image-section-new">
                {!isMobile && (
                <div className="scan-qr-code">
                  <img data-gumlet="false" src="/img/referrer/referrerPage_qr_code 1.svg" alt="qr code" />
                  <p data-key="referrer::scan" data-lokalise="">{referralLandingPage.scan}</p>
                </div>
                )}
              </div>
            </div>
          </section>
          <Container>
            {country === 'tw' && (
              <CampaignSliderTW setImageActive={setImageActive} activeImage={activeImage} />
            )}
            <div className="how-does-it-work">
              <div className="how-does-title-new">
                <h2 data-key="referrer::how_does_heading" data-lokalise="">{referralLandingPage.how_does_heading}</h2>
              </div>
              <div className="step-section-wrapper-new">
                {referralLandingPage.how_does_it_work.map((step, index) => (
                  <div className={`step-box-bg-wrapper box-${index}-content ${country}-${lang}`}>
                    <div className="step-box-content">
                      <div className="step-count-box">
                        <img data-gumlet="false" src={`/img/referrer/${index + 1}.png`} alt="count" />
                      </div>
                      <h3
                        className="title-step-box"
                        data-key={`referrer::how_does_it_work::${index}::title`}
                        data-lokalise=""
                      >
                        {step.title.replace('{referrer_amount}', referralAmount.referrerAmount)}
                      </h3>
                      <p
                        className={`content-step-box ${index + 1 === 3 ? 'box-three-content' : ''}`}
                        data-key={`referrer::how_does_it_work::${index}::description`}
                        data-lokalise=""
                      >
                        {step.description.replace('{referrer_amount}', referralAmount.referrerAmount).replace('{referred_amount}', referralAmount.referredAmount)}
                      </p>
                    </div>
                    <div className={`box-image ${index + 1 === 3 ? 'box-three-image' : ''}`}>
                      <img
                        data-gumlet="false"
                        src={`/img/referrer/steps/step${index + 1}_${i18n.activeLocale}_${isMobile ? 'mobile' : 'desktop'}.png`}
                        alt={step.alt_text}
                      />
                    </div>
                    { index + 1 === 3 && (
                    <div className="box-bottom-content">
                      <p
                        className={`${country !== 'sg' ? 'bottom-text' : ''}`}
                        data-key="referrer::box3content"
                        data-lokalise=""
                      >
                        {t('referrer.box3content', { referrer_amount: referralAmount.referrerAmount, referred_amount: referralAmount.referredAmount })}
                      </p>
                    </div>)}
                  </div>
                ))}
              </div>
            </div>

            <div className="faq-section-wrapper">
              <div
                className="title-faq"
              >
                <h4 data-key="referrer::questions" data-lokalise="">
                  {referralLandingPage.questions}
                </h4>
              </div>
              <div className="faq-section">
                {referralLandingPage.referrer_faq_questions_new.map((referrerFaq, index) => (
                  <div className="faq-collapse-wrapper" id={`collapse${index + 1}`}>
                    <Collapse
                      title={referrerFaq.question}
                      content={getFAQContent(`referrer.referrer_faq_questions_new.${index}.answer`)}
                      type="Referrals"
                      itemNumber={index + 1}
                      setIsCollapseOpen={setIsCollapseActive}
                      isCollapseOpen={isCollapseActive === index}
                      Lkey={`referrer::referrer_faq_questions_new::${index}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </Container>
        </main>
      </div>
    </Layout>
  );
};

export default ReferralsMechanism;
